import * as React from "react";

import BOTable, {
  BOColumn,
  BOColumns,
  BOBody,
  BORow,
  BOCell,
  BOPagination,
  BOFooter
} from "app/components/BOComponents/BOTable/BOTable";

import { formatStatusColour, formatColour } from "app/utils/filters";
import { PaginationProps } from "app/utils/withPagination";

import Config from "./Config";

export type ColumnRenderer = React.SFC<any> | ((row: any) => string);
export type ColumnConfig = {
  name: string;
  cellProps?: object;
  alwaysShow?: boolean;
  render: ColumnRenderer;
};

type TableConfig = {
  name: string;
  columnConfigs: ColumnConfig[];
  defaultColumns?: string[];
};

type Props = TableConfig &
  PaginationProps & {
    rows: any[];
    rowToLink?: (row: any) => string;
    history?: any;
  };

type State = {
  selectedColumns: string[];
};

export default class ConfigurableTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const selectedColumns = this.loadSelectedColumns();

    this.state = {
      selectedColumns
    };
  }

  selectedColumnConfigs() {
    return this.props.columnConfigs.filter(
      config =>
        this.state.selectedColumns.includes(config.name) || config.alwaysShow
    );
  }

  storageKey = () => `table_config_${this.props.name}`;

  columns() {
    const columns = this.selectedColumnConfigs();
    return (
      <BOColumns>
        {columns.map(({ name, render, cellProps }, i) => {
          return (
            <BOColumn key={i} {...cellProps}>
              {name}
            </BOColumn>
          );
        })}
      </BOColumns>
    );
  }

  body() {
    return (
      <BOBody
        onClick={event => {
          const parent = event.target.parentNode;
          const href = parent.getAttribute("href");
          const history = this.props.history;
          if (history && parent.tagName === "TR" && href) {
            history.push(href);
          }
        }}
      >
        {this.props.rows.map((row, i) => (
          <BORow
            href={this.props.rowToLink && this.props.rowToLink(row)}
            key={i}
            rowColor={
              row.color
                ? formatColour(row.color)
                : formatStatusColour(row.status)
            }
          >
            {this.selectedColumnConfigs().map((columnConfig, j) => {
              return (
                <BOCell key={j} {...columnConfig.cellProps}>
                  {columnConfig.render(row)}
                </BOCell>
              );
            })}
          </BORow>
        ))}
      </BOBody>
    );
  }

  footer() {
    const { pagination } = this.props;
    const allColumnNames = this.props.columnConfigs.map(config => config.name);

    return (
      <BOFooter>
        <BOPagination
          offset={pagination.offset}
          limit={pagination.limit}
          dataLength={this.props.rows.length}
          onPrev={this.props.pagination.previousPage}
          onNext={this.props.pagination.nextPage}
        />
        <Config
          columnConfigs={this.props.columnConfigs}
          selectedColumns={this.state.selectedColumns || allColumnNames}
          defaultColumns={this.props.defaultColumns || allColumnNames}
          onSave={this.updateSelectedColumns}
        />
      </BOFooter>
    );
  }

  saveSelectedColumns = (selectedColumns: string[]) => {
    try {
      localStorage.setItem(
        this.storageKey(),
        JSON.stringify({ selectedColumns })
      );
    } catch (e) {
      console.error("Unable to store table config", e);
    }
  };

  loadSelectedColumns = (): string[] => {
    try {
      const item = localStorage.getItem(this.storageKey());
      if (item) {
        return JSON.parse(item).selectedColumns;
      }
    } catch (e) {
      console.error("Unable to load table config", e);
    }

    return (
      this.props.defaultColumns ||
      this.props.columnConfigs.map(config => config.name)
    );
  };

  updateSelectedColumns = (selectedColumns: string[]) => {
    this.saveSelectedColumns(selectedColumns);
    this.setState({ selectedColumns });
  };

  render() {
    return (
      <>
        <BOTable
          name={this.props.name}
          columns={this.columns()}
          body={this.body()}
          footer={this.footer()}
          loading={false}
        />
      </>
    );
  }
}
